import { ModalTitle } from '../IntegrationInitializationModal'

import { Modal } from 'almanac-ui/Modal'
import { ChevronDown, Download } from 'blaze-ui/icons'
import WordPressLogo from 'almanac-ui/icons/24/WordPressLogo'
import Styles from './WordPressInitialization.module.scss'
import { Button } from 'blaze-ui/Button'
import { Text } from 'blaze-ui/Text/Text'
import { BLAZE_WORDPRESS_PLUGIN_HELP_CENTER_URL } from 'paths'
import { useState } from 'react'
import { InstallationInstructions } from './InstallationInstructions'
import { WordPressForm } from './WordPressForm'
import { ButtonLink } from 'blaze-ui/Button/ButtonLink'
import { ConditionalWrapper } from 'common/ConditionalWrapper/ConditionalWrapper'
import useResponsiveDesign from 'hooks/useResponsiveDesign'
import { IntegrationHelpButton } from 'components/IntegrationHelpButton/IntegrationHelpButton'
import { ExternalIntegration } from 'api/clients/ExternalIntegrationsClient'
import { useIsSystemRedesign } from 'hooks/useIsSystemRedesign'

const PLUGIN_URL = 'https://blaze-media-uploads.s3.us-west-1.amazonaws.com/blaze.zip'

const InstructionStep = ({
  title,
  placeholder,
  button,
  children,
}: {
  title: string
  placeholder?: string
  button: React.ReactNode
  isLink?: boolean
  children?: React.ReactNode
}) => (
  <div className={Styles.instructionItem}>
    <span className={Styles.instructionItemTitle}>
      {title}
      <span className={Styles.required}> *</span>
    </span>
    {children || (
      <div className={Styles.instructionItemContent}>
        {button}
        {placeholder && <Text variant="placeholder">{placeholder}</Text>}
      </div>
    )}
  </div>
)

interface Props {
  step: number
  setStep: React.Dispatch<React.SetStateAction<number>>
  onSubmit?: (integration?: ExternalIntegration) => void
  switchFromIntegrationId?: number
}

export const WordPressInitializationContent = ({ step, setStep, onSubmit, switchFromIntegrationId }: Props) => {
  const isSystemRedesignEnabled = useIsSystemRedesign()
  const [pluginInstallationInstructionsVisible, setPluginInstallationInstructionsVisible] = useState(false)
  const { isMobile } = useResponsiveDesign()

  return (
    <>
      {step === 1 && (
        <ConditionalWrapper
          condition={!isMobile}
          wrapper={(children) => <Modal.Content withoutFooter={isSystemRedesignEnabled}>{children}</Modal.Content>}
        >
          <div className={Styles.instructions}>
            <InstructionStep
              title="1. Download Blaze plugin"
              placeholder="Less than 1MB"
              button={
                <ButtonLink
                  to={PLUGIN_URL}
                  children="Download Plugin"
                  frontIcon={Download}
                  download={true}
                  variant="secondary"
                />
              }
            />

            <InstructionStep
              title="2. Install Blaze plugin from your admin panel"
              placeholder="Takes less than 2 minutes"
              button={
                <Button
                  children="Show Me How"
                  onPress={() => setPluginInstallationInstructionsVisible(true)}
                  endIcon={ChevronDown}
                  variant="secondary"
                />
              }
            >
              {pluginInstallationInstructionsVisible && <InstallationInstructions />}
            </InstructionStep>
            <InstructionStep
              title="3. Set Up Your Account"
              button={
                <Button aria-label="Set Up Your Account" onPress={() => setStep(2)} variant="primary">
                  Set Up Your Account
                </Button>
              }
            />
            <div className={Styles.footer}>
              <div>
                <Text variant="placeholder">
                  Note: If your site is hosted by WordPress,{' '}
                  <a href="https://wordpress.com/support/plan-features/business-plan/" target="_blank" rel="noreferrer">
                    WordPress Creator Plans
                  </a>
                  <span> are required to use plugins.</span>
                </Text>
              </div>
              <div>
                <Text variant="placeholder">
                  You can find more detailed information in our{' '}
                  <a href={BLAZE_WORDPRESS_PLUGIN_HELP_CENTER_URL} target="_blank" rel="noreferrer">
                    Help Center.
                  </a>
                </Text>
              </div>
            </div>
          </div>
        </ConditionalWrapper>
      )}
      {step === 2 && (
        <WordPressForm
          onBack={() => setStep(1)}
          onSubmit={onSubmit}
          switchFromIntegrationId={switchFromIntegrationId}
        />
      )}
    </>
  )
}

interface WordPressInitializationProps {
  onConnect?: (integration?: ExternalIntegration) => void
  switchFromIntegrationId?: number
}

const WordPressInitialization = ({ onConnect, switchFromIntegrationId }: WordPressInitializationProps = {}) => {
  const [step, setStep] = useState(1)

  return (
    <Modal.Root>
      <Modal.Header
        title={<ModalTitle icon={<WordPressLogo size={32} />} title="WordPress Integration" />}
        actions={<IntegrationHelpButton size="sm" integration="WordPress" />}
      />
      <WordPressInitializationContent
        step={step}
        setStep={setStep}
        onSubmit={onConnect}
        switchFromIntegrationId={switchFromIntegrationId}
      />
    </Modal.Root>
  )
}

export default WordPressInitialization
